<template>
  <button
    type="button"
    class="kp-dl-icon"
    :aria-label="ariaLabel"
    :disabled="disabled"
    @click.stop.prevent="$emit('click', $event)"
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <g class="kp-dl-icon__arrow">
        <line
          x1="12"
          y1="3"
          x2="12"
          y2="15"
        />
        <polyline points="7 10 12 15 17 10" />
      </g>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    </svg>
    <span
      class="kp-dl-icon__tip"
      role="tooltip"
    >{{ label }}</span>
  </button>
</template>

<script>
// KP-3919: the download affordance on a collection card. `.stop.prevent` is
// on the button itself because both hosts render it inside a clickable card —
// one of them inside a <b-link> — and a bubbling click would navigate away
// instead of opening the download popup.
// Both card surfaces
// had their own copy — same geometry, same colours, same 1.6s drop, same
// 2s-delay tooltip — and had already started to drift apart in class naming
// and colour-token fallbacks. One component, one animation, one place to fix.
export default {
  name: 'DownloadIconButton',
  props: {
    label: { type: String, default: 'Download' },
    ariaLabel: { type: String, default: 'Download' },
    disabled: { type: Boolean, default: false },
  },
}
</script>

<style lang="scss" scoped>
.kp-dl-icon {
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 9px;
  background: #efeefc;
  color: #5a63d8;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease,
    transform 0.12s ease;

  &:hover,
  &:focus-visible {
    background: #6a73e8;
    color: #ffffff;
    box-shadow: 0 5px 12px -6px rgba(106, 115, 232, 0.55);
  }

  // The arrow "downloads" on approach: drops into the tray, fades, and falls
  // again from the top while hovered.
  &:hover .kp-dl-icon__arrow,
  &:focus-visible .kp-dl-icon__arrow {
    animation: kp-dl-icon-drop 1.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  }

  &:focus-visible {
    outline: 2px solid rgba(106, 115, 232, 0.55);
    outline-offset: 2px;
  }

  &:active {
    transform: scale(0.94);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__tip {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translate(-50%, 4px);
    padding: 5px 10px;
    border-radius: 7px;
    // Falls back for the surfaces that do not define the v2 tokens.
    background: var(--v2-foreground, #1a0a3e);
    color: #ffffff;
    font-family: var(--v2-font-sans, inherit);
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease, transform 0.2s ease;
    z-index: 5;

    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 4px solid transparent;
      border-top-color: var(--v2-foreground, #1a0a3e);
    }
  }

  &:hover .kp-dl-icon__tip,
  &:focus-visible .kp-dl-icon__tip {
    opacity: 1;
    transform: translate(-50%, 0);
    // The tooltip waits out a deliberate hover before appearing.
    transition-delay: 2s;
  }
}

@keyframes kp-dl-icon-drop {
  0% {
    transform: translateY(-3px);
    opacity: 0;
  }
  30% {
    transform: translateY(0);
    opacity: 1;
  }
  70% {
    transform: translateY(3px);
    opacity: 1;
  }
  100% {
    transform: translateY(5px);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .kp-dl-icon,
  .kp-dl-icon__arrow,
  .kp-dl-icon__tip {
    transition: none;
    animation: none;
  }
}
</style>
